/**
 * 파일명 : usr0104m.jsx
 * 설명 : 연차관리 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.1       2024-03-19   방진선     검색조건 수정
 *     
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0104mAction } from '../../../redux/slice/usr/usr0104m_slice';
import { IDeptCdState, IInquiryCndsState, IUserListItemState, IUserStCdState, IUserItemState } from '../../../redux/models/usr/usr0104m_model';

/*  AntD  */
import { Button, Space, Input, Select, Row, Col, Layout, Typography, Collapse, DatePicker, ConfigProvider, Table  } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';
import dayjs from 'dayjs';

const Usr0104m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
	/** State / Ref / Reducer 선언 **/
    const dispatch = useAppDispatch();
    const deptCdList   = useAppSelector((state) => state.Usr0104m.deptCdList);
    const userStCdList = useAppSelector((state) => state.Usr0104m.userStCdList);
    const userList     = useAppSelector((state) => state.Usr0104m.userList);
    const userDatas    = useAppSelector((state) => state.Usr0104m.userDatas);
    const inquiryCnds  = useAppSelector((state) => state.Usr0104m.inquiryCnds);
    const current      = useAppSelector((state) => state.Usr0104m.current);
    const pageSize     = useAppSelector((state) => state.Usr0104m.pageSize);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text } = Typography;
    const { Panel } = Collapse;

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        const initInquiryCnds =  {
            empNo : '',
            userId : '',
            userNm : '',
            deptCd : '',
            userStCd : 'A',
            baseYy  : commonDate.maskDate(commonDate.getToday(), 'YYYY')  // 기준년월
        };

        dispatch(Usr0104mAction.setInquiryCnds(initInquiryCnds));

        fn_User();
        fn_DeptCds();
        setUserStCdList();
        fn_Search(initInquiryCnds);
    }, []);     // eslint-disable-line react-hooks/exhaustive-deps


    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Usr0104mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        let arrUserOptions: IUserItemState[] = [];

        if (common.isAdmin()) {
            arrUserOptions.push({label: '전체', value: ''});
        }

        if (userList && userList.length > 0) {
            userList.forEach((item:IUserItemState) => {
                arrUserOptions.push({
                    label: item.userNm,
                    value: item.empNo
                });
            });
        }

        return arrUserOptions;
    }, [userList]);



    /**
     * 기능 : fn_DeptCds
     * 설명 : 부서코드 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_DeptCds = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            deptStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0005',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.deptItems.sort((a:IDeptCdState, b:IDeptCdState) => (a.deptNm > b.deptNm) ? 1 : -1)
                dispatch(Usr0104mAction.setDeptCdList(responseData.deptItems));
            },
        });
    };

    const deptOptions = React.useCallback(() => {
        let arrOptions: IDeptCdState[] = [];

        if (common.isAdmin()) {
            arrOptions.push({label: '전체', value: ''});
        }

        deptCdList.forEach((item:IDeptCdState) => {
            arrOptions.push({
                label: item.deptNm,
                value: item.deptCd
            });
        });

        return arrOptions;
    }, [deptCdList]);


    const setUserStCdList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IUserStCdState[]) => {
                let arrOptions:IUserStCdState[] = [];

                arrOptions.push({label: '전체', value: ''});

                datas.forEach((item:IUserStCdState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });

                dispatch(Usr0104mAction.setUserStCdList(arrOptions));
            }
        };
        common.getComCodeList('003', option);
    }

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>성명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'center',
                width: 120,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>입사일자</div>,
                dataIndex: 'joinDt',
                key: 'joinDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: 100,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: 100,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'center',
                width: 150,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>기본연차</div>,
                key: 'giveOffDays',
                dataIndex: 'giveOffDays',
                width: 80,
                align: 'center',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>추가연차</div>,
                key: 'addOffDays',
                dataIndex: 'addOffDays',
                width: 80,
                align: 'center',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>비고</div>,
                key: 'rmk',
                dataIndex: 'rmk',
                width: 300,
                align: 'center',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>총부여일</div>,
                dataIndex: 'tOffDays',
                key: 'tOffDays',
                align: 'center',
                width: 80,
                render: (_:string, record:IUserListItemState) => record.giveOffDays + record.addOffDays
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>사용일수</div>,
                dataIndex: 'useOffDays',
                key: 'useOffDays',
                align: 'center',
                width: 80,
            },];
            return arr;
    }, []);

    const adminColumns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>성명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'center',
                width: 120,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>입사일자</div>,
                dataIndex: 'joinDt',
                key: 'joinDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: 100,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: 100,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'center',
                width: 150,
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>기본연차</div>,
                key: 'giveOffDays',
                dataIndex: 'giveOffDays',
                width: 80,
                align: 'center',
                render: (_:string, record:IUserListItemState) => (
                    <Input type='number' style={{ width: 80, textAlign: 'center' }} value={record.giveOffDays} onChange={(e:any) => {
                        if (e.currentTarget.value % 0.5 === 0) {
                            const newRecord = {...record, giveOffDays : e.currentTarget.value};
                            dispatch(Usr0104mAction.setUserItem(newRecord));
                        }
                    }}/>
                ),
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>추가연차</div>,
                key: 'addOffDays',
                dataIndex: 'addOffDays',
                width: 80,
                align: 'center',
                render: (_:string, record:IUserListItemState) => (
                    <Input type='number' style={{ width: 80, textAlign: 'center' }} value={record.addOffDays} onChange={(e:any) => {
                        if (e.currentTarget.value % 0.5 === 0) {
                            const newRecord = {...record, addOffDays : e.currentTarget.value};
                            dispatch(Usr0104mAction.setUserItem(newRecord));
                        }
                    }}/>
                ),
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>비고</div>,
                key: 'rmk',
                dataIndex: 'rmk',
                width: 300,
                align: 'center',
                render: (_:string, record:IUserListItemState) => (
                    <Space>
                        <Input key={record.empNo + '_rmk'} id={record.empNo + '_rmk'} style={{ width: 350, textAlign: 'left' }} value={record.rmk} onChange={(e:any) => {
                            const newRecord = {...record, rmk : e.currentTarget.value};
                            dispatch(Usr0104mAction.setUserItem(newRecord));
                        }}/>
                    </Space>
                ),
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>총부여일</div>,
                dataIndex: 'tOffDays',
                key: 'tOffDays',
                align: 'center',
                width: 80,
                render: (_:string, record:IUserListItemState) => parseFloat(String(record.giveOffDays)) + parseFloat(String(record.addOffDays))
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>사용일수</div>,
                dataIndex: 'useOffDays',
                key: 'useOffDays',
                align: 'center',
                width: 80,
            },];
            return arr;
    }, []);


    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Usr0104mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Usr0104mAction.setUserDatas([]));
    };

    /**
     *  기능 : DatePicker 데이터 변경
     *  설명 : DatePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const calendar_onChange = (_:any, dateStrings:any) => {
        dispatch(Usr0104mAction.setInquiryCnds({...inquiryCnds, baseYy: dateStrings}));
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            case 'btn_giveAll': {
                for (let i = 0; i < userList.length; i++) {
                    const newRecord:IUserListItemState = {...userList[i], giveOffDays : 15};
                    dispatch(Usr0104mAction.setUserItem(newRecord));
                }

                const param = {
                    title : '위드정보 확인',
                    contents: '부여휴가일수가 15일로 일괄 셋팅 되었습니다.\n[부여] 버튼을 클릭하여 적용하세요.',
                };
                common.openAlert(param);
                break;
            }

            case 'btn_give': {
                if (!userDatas || userDatas.length < 1) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '휴가부여대상 조회를 수행하세요.',
                    };
                    common.openAlert(param);
                    return false;
                } else {
                    const filterList = userDatas.filter((item:IUserListItemState) => {
                        return item.addOffDays > 0 && common.isEmptyString(item.rmk);
                    });

                    if (filterList && filterList.length > 0) {
                        let msg = filterList.length === 1 ? filterList[0].userNm : filterList[0].userNm + ' 외 ' + (filterList.length - 1) + '명'
                        msg += '\n추가부여 사유를 입력하세요.'; 

                        const param = {
                            title : '위드정보 확인',
                            contents: msg,
                        };
                        common.openInfo (param);
                        return false;
                    } else {
                        const param = {
                            title : '위드정보 확인',
                            contents: '연차휴가를 부여 하시겠습니까?',
                            accept: () => {
                                /* 변수선언 및 셋팅 영역 */
                                // 입력파라미터
                                const requestData = {
                                    ...inquiryCnds,
                                    userDtos : userDatas,
                                };

                                /* 트렌젝션 호출 및 결과 셋팅 */
                                commonProxy.callAsyncSvc(requestData, {
                                    txCd: 'TRUSRS0006',
                                });
                            }
                        };
                        common.openConfirm(param);
                    }

                    break;
                }
            }
            default:
        }
    };

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = (searchCnds?: IInquiryCndsState) => {
        dispatch(Usr0104mAction.setCurrent(1));

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData:IInquiryCndsState = {
            ...(searchCnds ? searchCnds : inquiryCnds)
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0005',
            success: (responseData:any) => {
                dispatch(Usr0104mAction.setUserDatas(responseData.userList));
            },
        });
    };

    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const handleOnKeyUp = (e:any)=> {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };


    return (
        <>
            <Layout>
                <Content>
                    <Collapse
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Space>
                                    <Col>
                                        <Space>
                                            <label>기준년도</label>
                                            <ConfigProvider locale={locale}>
                                                <DatePicker onChange={calendar_onChange} value={!inquiryCnds.baseYy ? null : dayjs(inquiryCnds.baseYy)} picker='year'/>
                                            </ConfigProvider>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label>직원명</label>
                                            <Select 
                                                showSearch
                                                style={{
                                                    width: 100,
                                                }}
                                                optionFilterProp='label'
                                                defaultValue=''
                                                filterSort={(optionA: any, optionB: any) => {
                                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                                    
                                                    if (labelA === '전체') return -1;
                                                    if (labelB === '전체') return 1;
                                                    
                                                    return labelA.localeCompare(labelB);
                                                }}
                                                options={userOptions()}
                                                onKeyUp={handleOnKeyUp}
                                                value={inquiryCnds.empNo}
                                                onChange={val => dispatch(Usr0104mAction.setInquiryCnds({...inquiryCnds, empNo: val}))}
                                            ></Select>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label>부서</label>
                                            <Select
                                                style={{
                                                    width: 200,
                                                }}
                                                optionFilterProp='label'
                                                options={deptOptions()}
                                                onKeyUp={handleOnKeyUp}
                                                onChange={val => {
                                                    dispatch(Usr0104mAction.setInquiryCnds({...inquiryCnds, deptCd: val}))
                                                }}
                                                value={inquiryCnds.deptCd}
                                            />
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label>사용자상태</label>
                                            <Select 
                                                style={{
                                                    width: 80,
                                                }}
                                                defaultValue='A'
                                                optionFilterProp='label'
                                                options={userStCdList}
                                                onKeyUp={handleOnKeyUp}
                                                onChange={val => {
                                                    dispatch(Usr0104mAction.setInquiryCnds({...inquiryCnds, userStCd: val}))
                                                }}
                                                value={inquiryCnds.userStCd}
                                            />
                                        </Space>
                                    </Col>
                                </Space>
                                <Col>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={onClick}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={onClick}
                                        >
                                            조회
                                        </Button>
                                        {common.isAdmin() ?
                                            <>
                                                <Button
                                                    name='btn_giveAll'
                                                    className='background-color-default-button'
                                                    onClick={onClick}
                                                >
                                                    일괄입력(15일)
                                                </Button>
                                                <Button
                                                    name='btn_give'
                                                    className='background-color-primary-button'
                                                    onClick={onClick}
                                                >
                                                    부여
                                                </Button>
                                            </>
                                            : null}
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>연차사용자목록</Text>
                            </Space>
                        </Col>
                    </Row>
                    <Table
                        dataSource={userDatas}
                        key='empNo'
                        columns={common.isAdmin() ? adminColumns() : columns()}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + userDatas.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Usr0104mAction.setCurrent(page));
                                dispatch(Usr0104mAction.setPageSize(pageSize));
                            },
                        }}
                    />
                </Content>
            </Layout>
        </>
    );
};

export default Usr0104m;

